import { Alert, Button, EmptyState, FieldBlock, Input, LoadingState, Select, Textarea, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { CamposDaEntidade } from '../admin/CamposDaEntidade.tsx'
import { api } from '../../lib/api.ts'
import { SwitchCard } from '../admin/catalog-shared.tsx'

/**
 * Cadastro de PESSOAS (pedido do usuario 2026-08-25): contatos do mundo
 * externo — nao confundir com Membros (usuarios do sistema). Uma pessoa pode
 * pertencer a VARIAS empresas (N:N), com a FUNCAO dela em cada empresa; o
 * vinculo tambem e editavel pelo cadastro da empresa. Padrao FormScreen.
 */

type PeopleList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.catalog)['people']['$get']>>['json']>>
type PersonRow = PeopleList['people'][number]

type FormPessoa = {
  name: string
  email: string
  phone: string
  notes: string
  isActive: boolean
  /** EMPRESAS da pessoa (N:N) com a funcao dela em cada uma. */
  companies: { companyId: string; role: string }[]
  /** Campos complementares dos TOPICOS de Pessoa (fase 6). */
  customValues: Record<string, unknown>
}

const VAZIO: FormPessoa = { name: '', email: '', phone: '', notes: '', isActive: true, companies: [], customValues: {} }

export function PeopleSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()

  const lista = useQuery({
    queryKey: ['people'],
    queryFn: async () => (await api.catalog.people.$get()).json(),
  })
  const empresas = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await api.catalog.companies.$get()).json(),
  })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormPessoa>(VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) abrir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['people'] })
    void qc.invalidateQueries({ queryKey: ['companies'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const linksDe = (f: FormPessoa) =>
    f.companies.filter((l) => l.companyId !== '').map((l) => ({ companyId: l.companyId, role: l.role.trim() || null }))

  const listaDe = (texto: string) => texto.split(',').map((t) => t.trim()).filter(Boolean)

  const corpoDe = (f: FormPessoa) => ({
    name: f.name.trim(),
    emails: listaDe(f.email),
    phones: listaDe(f.phone),
    notes: f.notes.trim() || null,
    isActive: f.isActive,
    customValues: f.customValues,
  })

  const criar = useMutation({
    mutationFn: async (f: FormPessoa) =>
      (await api.catalog.people.$post({ json: { ...corpoDe(f), companies: linksDe(f) } })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Pessoa criada.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormPessoa }) => {
      const salvo = await (await api.catalog.people[':id'].$patch({ param: { id }, json: corpoDe(f) })).json()
      await api.catalog.people[':id'].companies.$put({ param: { id }, json: { links: linksDe(f) } })
      return salvo
    },
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Pessoa atualizada.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog.people[':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.catalog.people[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Pessoa excluída.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const people = lista.data?.people ?? []
  const emEdicao = editando?.id ? people.find((p) => p.id === editando.id) : undefined

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return people
    return people.filter((p) =>
      `${p.name} ${p.emails.join(' ')} ${p.phones.join(' ')} ${p.companies.map((c) => c.companyName).join(' ')}`
        .toLowerCase()
        .includes(termo),
    )
  }, [people, busca])

  const formDe = (alvo: PersonRow): FormPessoa => ({
    name: alvo.name,
    email: alvo.emails.join(', '),
    phone: alvo.phones.join(', '),
    notes: alvo.notes ?? '',
    isActive: alvo.isActive,
    companies: alvo.companies.map((c) => ({ companyId: c.companyId, role: c.role ?? '' })),
    customValues: ((alvo as { customValues?: Record<string, unknown> }).customValues ?? {}),
  })

  const abrir = (alvo: PersonRow | null) => {
    setForm(alvo ? formDe(alvo) : VAZIO)
    setEditando({ id: alvo?.id ?? null })
  }

  const assinatura = (f: FormPessoa) => JSON.stringify([corpoDe(f), linksDe(f)])
  const alterado =
    editando?.id == null
      ? form.name.trim().length >= 2
      : emEdicao !== undefined && assinatura(form) !== assinatura(formDe(emEdicao))

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const salvarValido = alterado && form.name.trim().length >= 2 && !criar.isPending && !atualizar.isPending
    const opcoesDeEmpresa = (empresas.data?.companies ?? []).filter((e) => e.isActive)

    return (
      <FormScreen
        title={editando.id ? 'Editar pessoa' : 'Nova pessoa'}
        subtitle={editando.id ? emEdicao?.name : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar pessoa'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Dados da pessoa">
          <FormRow cols="2fr 1fr 1fr">
            <FieldBlock label="Nome" required>
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.name}
                  placeholder="Ex.: Marina Duarte"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="E-mails" hint="Separe por vírgula.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.email}
                  placeholder="marina@empresa.com.br, outra@…"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Telefones" hint="Separe por vírgula.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.phone}
                  placeholder="(61) 99999-0000, …"
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="1fr">
            <FieldBlock label="Observações">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="minmax(220px, 420px)">
            <SwitchCard
              title={form.isActive ? 'Pessoa ativa' : 'Pessoa inativa'}
              description={form.isActive ? 'Aparece nas opções de vínculo das empresas.' : 'Some das opções; vínculos existentes ficam.'}
              checked={form.isActive}
              onChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
            />
          </FormRow>
        </FormSection>

        {/* EMPRESAS da pessoa (o MESMO vinculo N:N editado pela empresa) */}
        {/* Topicos de PESSOA (fase 6) */}
        <CamposDaEntidade
          entity="person"
          valores={form.customValues}
          onMudar={(customValues) => setForm((f) => ({ ...f, customValues }))}
        />

        <FormSection title={`Empresas${form.companies.length > 0 ? ` (${form.companies.length})` : ''}`}>
          {form.companies.map((vinculo, indice) => {
            const jaEscolhidas = form.companies.filter((_, i) => i !== indice).map((l) => l.companyId)
            return (
              <FormRow key={indice} cols="2fr 2fr auto">
                <FieldBlock label={indice === 0 ? 'Empresa' : ''}>
                  {({ id }) => (
                    <Select<string>
                      id={id}
                      value={vinculo.companyId || null}
                      placeholder="Escolha a empresa"
                      options={opcoesDeEmpresa
                        .filter((e) => !jaEscolhidas.includes(e.id))
                        .map((e) => ({ value: e.id, label: e.name }))}
                      onChange={(companyId) =>
                        setForm((f) => ({
                          ...f,
                          companies: f.companies.map((l, i) => (i === indice ? { ...l, companyId } : l)),
                        }))
                      }
                    />
                  )}
                </FieldBlock>
                <FieldBlock label={indice === 0 ? 'Função na empresa' : ''}>
                  {({ id }) => (
                    <Input
                      id={id}
                      value={vinculo.role}
                      placeholder="Ex.: Gerente de TI"
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          companies: f.companies.map((l, i) => (i === indice ? { ...l, role: e.target.value } : l)),
                        }))
                      }
                    />
                  )}
                </FieldBlock>
                <FieldBlock label={indice === 0 ? ' ' : ''}>
                  {() => (
                    <Button
                      variant="ghost"
                      icon="fas fa-trash-alt"
                      aria-label="Remover empresa da pessoa"
                      onClick={() => setForm((f) => ({ ...f, companies: f.companies.filter((_, i) => i !== indice) }))}
                    >
                      Remover
                    </Button>
                  )}
                </FieldBlock>
              </FormRow>
            )
          })}
          <FormRow cols="minmax(200px, 300px)">
            <Button
              variant="secondary"
              icon="fas fa-building"
              disabled={opcoesDeEmpresa.length === 0}
              onClick={() => setForm((f) => ({ ...f, companies: [...f.companies, { companyId: '', role: '' }] }))}
            >
              Adicionar empresa
            </Button>
          </FormRow>
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Pessoas</h1>
        <p className="adm-head__desc">Contatos das empresas — cada pessoa pode pertencer a várias, com a função em cada uma.</p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando as pessoas" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar as pessoas"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome, contato ou empresa…" aria-label="Filtrar pessoas"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {people.length} {people.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-address-book"
            title={people.length === 0 ? 'Nenhuma pessoa ainda' : 'Nenhuma pessoa com esse filtro'}
            description={people.length === 0
              ? 'Cadastre a primeira pessoa e vincule às empresas em que ela atua.'
              : `Nada casa com “${busca}”.`}
            action={people.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Nova pessoa</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtrados.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const contatos = [...row.phones, ...row.emails].join(' · ')
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon"><i className="fas fa-user" aria-hidden /></span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name}</span>
                  <span className="adm-catrow__sub">{contatos || 'Sem contato'}</span>
                  {/* EMPRESAS em chips proprios (pedido 2026-08-25): sempre
                      visiveis — com a funcao, ou o aviso "Sem empresa" */}
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                    {row.companies.length > 0 ? (
                      row.companies.map((c) => (
                        <span
                          key={c.companyId}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '1px 8px',
                            borderRadius: 999,
                            background: 'var(--ob-primary-tint)',
                            color: 'var(--ob-primary)',
                          }}
                        >
                          <i className="fas fa-building" aria-hidden style={{ fontSize: 9 }} />
                          {c.companyName}
                          {c.role ? ` · ${c.role}` : ''}
                        </span>
                      ))
                    ) : (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '1px 8px',
                          borderRadius: 999,
                          background: 'var(--ob-background-3)',
                          color: 'var(--ob-text-muted)',
                        }}
                      >
                        Sem empresa
                      </span>
                    )}
                  </span>
                </span>
                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{row.name}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativa' : 'Inativa'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${row.name} está ativa`}
                        title={row.isActive ? 'Ativa — clique para desativar' : 'Inativa — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger"
                      aria-label={`Excluir ${row.name}`} title="Excluir"
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
